// Pure core for Xero Phase 1b — grouped-invoice line allocation (splitting ONE
// invoice line across several location accounts). Extends Phase 1's single-
// account resolution: a line's attribution becomes `allocations: [{accountId,
// shareBps}]` (basis points summing to 10000). A normal single-account line is
// just one 100% allocation, so the 92% of ordinary invoices are unchanged.
//
// Real case (INV-5835): contact "Anytime Fitness - TJ Singh Group" (→ the kyck
// GROUP account), one $650 line "Google Advertising Management - Anytime Fitness
// Takanini, Pukekohe, Glen Eden, Glendene" → split evenly across those four
// location accounts. It repeats monthly, so a confirmed split is remembered as a
// rule keyed by (tenant, contact, line-signature) and auto-applies thereafter.
//
// Framework-free + tested; reuses the xeroContactMatch fuzzy scorer so location
// matching behaves like the rest of the app.

import { symmetricNameScore } from './xeroContactMatch.js';
import { OPERATIONAL_ACCOUNT_TYPES } from './accountAnalytics.js';

export const TOTAL_BPS = 10000;
// A description location token matches an account at/above this score. Same bar
// as the contact suggester — the operator confirms multi-location splits anyway.
export const ALLOCATION_MATCH_BAR = 0.6;

export type XeroLineAllocation = { accountId: string; shareBps: number };

// Minimal account shape the allocator needs (from the roster). `status` is
// carried so the no-token push-down (Phase 1c) can restrict the even split to
// ACTIVE operational children; it's optional so legacy callers/fixtures without
// it simply don't qualify as active.
export type AllocationAccount = {
  id: string;
  name: string;
  accountType: string;
  parentAccountId: string | null;
  status?: string;
};

// ---- signature (rule key) --------------------------------------------------

export function normalizeAllocationText(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

// The rule key for a line. A repeating-template id (when Xero populates it) makes
// the key rock-solid; otherwise the normalized description (+ itemCode) is the
// key. In BOTH cases the description is part of the signature, so a changed
// location set (different description) yields a new signature → drops back to
// pending for re-confirmation (the desired safety behaviour).
export function lineSignature(input: {
  description?: string | null;
  itemCode?: string | null;
  repeatingInvoiceId?: string | null;
}): string {
  const desc = normalizeAllocationText(input.description ?? '');
  const item = (input.itemCode ?? '').trim().toLowerCase();
  const base = item ? `${item} ${desc}` : desc;
  const repeat = (input.repeatingInvoiceId ?? '').trim();
  return repeat ? `repeat:${repeat}|${base}` : base;
}

// ---- description parsing ---------------------------------------------------

// Pull the trailing comma-separated location list out of a line description.
// "Google Advertising Management - Anytime Fitness Takanini, Pukekohe, Glen Eden,
// Glendene" → ["Anytime Fitness Takanini", "Pukekohe", "Glen Eden", "Glendene"].
// The service segment before the FIRST " - " is dropped; the brand prefix rides
// on the first token (the fuzzy scorer's containment handles the brand-less
// suburb tokens against the full account names).
export function parseLocationTokens(description: string | null | undefined): string[] {
  if (!description) return [];
  let seg = description;
  const dash = description.indexOf(' - ');
  if (dash >= 0) seg = description.slice(dash + 3);
  return seg
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

// Best account for a token at/above the bar, or null. Stable (ties keep first).
export function bestAccountMatch(
  token: string,
  roster: AllocationAccount[],
  bar: number = ALLOCATION_MATCH_BAR,
): AllocationAccount | null {
  let best: { account: AllocationAccount; score: number } | null = null;
  for (const account of roster) {
    const score = symmetricNameScore(token, account.name);
    if (best === null || score > best.score) best = { account, score };
  }
  return best && best.score >= bar ? best.account : null;
}

// Match a token SCOPED to the group's descendants first (so "Takanini" resolves
// to THIS group's Takanini), falling back to the full roster only if unmatched.
export function matchLocationToken(
  token: string,
  scopedRoster: AllocationAccount[],
  fullRoster: AllocationAccount[],
  bar: number = ALLOCATION_MATCH_BAR,
): AllocationAccount | null {
  return bestAccountMatch(token, scopedRoster, bar) ?? bestAccountMatch(token, fullRoster, bar);
}

// ---- format-agnostic location scan (distinguishing tokens) -----------------

// The shortest distinguishing token we'll match on — guards against a stray
// short strip (an abbreviation, a lone initial) matching noise in a description.
export const MIN_DISTINGUISHING_TOKEN_LEN = 3;

// A name's normalized lowercase words ("Snap Fitness Glen Eden" → ['snap',
// 'fitness','glen','eden']).
function nameWords(name: string): string[] {
  return normalizeAllocationText(name).split(' ').filter((w) => w.length > 0);
}

// The length of the longest word-run shared as a LEADING prefix across every name
// (case-insensitive). Within a group like "Corporate NZ" every location is "Snap
// Fitness X", so the shared leading run is ['snap','fitness'] → 2.
export function commonLeadingWordCount(names: string[]): number {
  const lists = names.map(nameWords).filter((w) => w.length > 0);
  if (lists.length === 0) return 0;
  const minLen = Math.min(...lists.map((w) => w.length));
  let count = 0;
  for (let i = 0; i < minLen; i++) {
    const w = lists[0]![i]!;
    if (lists.every((list) => list[i] === w)) count++;
    else break;
  }
  return count;
}

// The DISTINGUISHING token of a name given the group's shared-leading-word count:
// the name's words after dropping that shared brand/common prefix, ALWAYS keeping
// at least one word (so a name that IS entirely the prefix, or a lone descendant,
// still yields its last word). Normalized lowercase, space-joined
// ("Snap Fitness Glen Eden", shared=2 → "glen eden").
export function distinguishingToken(name: string, sharedLeadingWords: number): string {
  const words = nameWords(name);
  if (words.length === 0) return '';
  const drop = Math.min(Math.max(sharedLeadingWords, 0), words.length - 1);
  return words.slice(drop).join(' ');
}

// The distinguishing token of every name in a sibling set (the shared prefix is
// computed once across the set, then stripped from each). Order-preserving.
export function distinguishingTokens(names: string[]): string[] {
  const shared = commonLeadingWordCount(names);
  return names.map((n) => distinguishingToken(n, shared));
}

// Does `tokenWords` occur as a contiguous word run in `descWords`? Word-boundary
// by construction (whole normalized words), so "fit" never matches inside
// "fitness" and a multi-word suburb ("glen eden") matches only as a unit.
function descriptionContainsWords(descWords: string[], tokenWords: string[]): boolean {
  if (tokenWords.length === 0) return false;
  for (let i = 0; i + tokenWords.length <= descWords.length; i++) {
    let hit = true;
    for (let j = 0; j < tokenWords.length; j++) {
      if (descWords[i + j] !== tokenWords[j]) {
        hit = false;
        break;
      }
    }
    if (hit) return true;
  }
  return false;
}

// Scan a description for ANY of the candidate accounts named anywhere in it —
// leading ("Barrington - …"), trailing comma-list (TJ Singh), or mid-string — by
// matching each account's distinguishing token (word-boundary, min length). The
// candidates are the mapped group's active operational descendants; matching on
// the DISTINGUISHING token (not the shared brand prefix) is what keeps service
// words ("Meta", "Google", "Advertising", "Lead", "Fit", "Package") from ever
// matching a location. Deduped, order-stable (candidate order).
export function scanDescriptionForLocations(
  description: string | null | undefined,
  candidates: AllocationAccount[],
): AllocationAccount[] {
  if (!description || candidates.length === 0) return [];
  const descWords = nameWords(description);
  if (descWords.length === 0) return [];
  const shared = commonLeadingWordCount(candidates.map((a) => a.name));
  const seen = new Set<string>();
  const matched: AllocationAccount[] = [];
  for (const account of candidates) {
    if (seen.has(account.id)) continue;
    const token = distinguishingToken(account.name, shared);
    const tokenWords = token.split(' ').filter((w) => w.length > 0);
    if (tokenWords.join('').length < MIN_DISTINGUISHING_TOKEN_LEN) continue;
    if (descriptionContainsWords(descWords, tokenWords)) {
      seen.add(account.id);
      matched.push(account);
    }
  }
  return matched;
}

// Parse a description into its matched location accounts (deduped, order-stable).
// Format-agnostic: FIRST a distinguishing-token scan over the group's active
// operational descendants (catches a location named anywhere — leading, mid, or
// trailing). ONLY when the scan finds nothing does it fall back to the legacy
// trailing comma-split + fuzzy path (so a format the scan can't see — a typo'd or
// abbreviated suburb — still resolves) — a fallback, not a blind union, so the
// fuzzy scorer can't spuriously add a SECOND match and turn a clean single-named
// line into a false multi.
export function parseAndMatchLocations(
  description: string | null | undefined,
  scopedRoster: AllocationAccount[],
  fullRoster: AllocationAccount[],
  bar: number = ALLOCATION_MATCH_BAR,
): AllocationAccount[] {
  const scanned = scanDescriptionForLocations(
    description,
    operationalActiveDescendants(scopedRoster),
  );
  if (scanned.length > 0) return scanned;

  const seen = new Set<string>();
  const matched: AllocationAccount[] = [];
  for (const token of parseLocationTokens(description)) {
    const account = matchLocationToken(token, scopedRoster, fullRoster, bar);
    if (account && !seen.has(account.id)) {
      seen.add(account.id);
      matched.push(account);
    }
  }
  return matched;
}

// ---- even split + cents distribution ---------------------------------------

// Even shareBps for n accounts summing to exactly TOTAL_BPS; the remainder is
// handed to the first `remainder` accounts (deterministic).
export function evenSplitBps(n: number): number[] {
  if (n <= 0) return [];
  const base = Math.floor(TOTAL_BPS / n);
  const remainder = TOTAL_BPS - base * n;
  return Array.from({ length: n }, (_, i) => base + (i < remainder ? 1 : 0));
}

export function buildEvenAllocations(accountIds: string[]): XeroLineAllocation[] {
  const bps = evenSplitBps(accountIds.length);
  return accountIds.map((accountId, i) => ({ accountId, shareBps: bps[i]! }));
}

// Distribute an integer-cent amount across allocations by shareBps so the pieces
// sum to EXACTLY amountCents (leftover cents handed out deterministically in
// order; sign-safe for the rare credit line).
export function distributeCentsByAllocations(
  amountCents: number,
  allocations: XeroLineAllocation[],
): { accountId: string; cents: number }[] {
  if (allocations.length === 0) return [];
  const pieces = allocations.map((a) => ({
    accountId: a.accountId,
    cents: Math.floor((amountCents * a.shareBps) / TOTAL_BPS),
  }));
  const assigned = pieces.reduce((sum, p) => sum + p.cents, 0);
  let leftover = amountCents - assigned;
  const step = leftover >= 0 ? 1 : -1;
  for (let i = 0; i < pieces.length && leftover !== 0; i++) {
    pieces[i]!.cents += step;
    leftover -= step;
  }
  return pieces;
}

// ---- group descendants -----------------------------------------------------

// Every account under `rootId` (its whole subtree via parentAccountId). Used to
// scope location matching to the group's own locations.
export function descendantAccounts(
  rootId: string,
  accounts: AllocationAccount[],
): AllocationAccount[] {
  const byParent = new Map<string, AllocationAccount[]>();
  for (const a of accounts) {
    if (a.parentAccountId) {
      const arr = byParent.get(a.parentAccountId) ?? [];
      arr.push(a);
      byParent.set(a.parentAccountId, arr);
    }
  }
  const out: AllocationAccount[] = [];
  const stack = [rootId];
  const seen = new Set<string>([rootId]);
  while (stack.length > 0) {
    const id = stack.pop()!;
    for (const child of byParent.get(id) ?? []) {
      if (seen.has(child.id)) continue; // cycle guard
      seen.add(child.id);
      out.push(child);
      stack.push(child.id);
    }
  }
  return out;
}

// The group's ACTIVE OPERATIONAL descendants (locations/dealerships) — the
// push-down target set for a group line that names no location (Phase 1c). Reuses
// the shared OPERATIONAL_ACCOUNT_TYPES so the definition stays in one place; the
// descendants list is already the group's subtree (from `descendantAccounts`),
// so this is just the operational-and-active filter over it.
const OPERATIONAL_TYPES: readonly string[] = OPERATIONAL_ACCOUNT_TYPES;

export function operationalActiveDescendants(
  descendants: AllocationAccount[],
): AllocationAccount[] {
  return descendants.filter(
    (d) => OPERATIONAL_TYPES.includes(d.accountType) && d.status === 'active',
  );
}

// ---- the resolver ----------------------------------------------------------

export type LineAllocationKind =
  | 'single'
  // ≥2 specifically named locations → even split across the named set.
  | 'multi'
  // No parseable location → pushed DOWN to the group's operational children.
  | 'group-all-children'
  // No parseable location AND no operational children → 100% on the group.
  | 'group-fallback'
  | 'unmapped';

export type LineAllocationResolution = {
  kind: LineAllocationKind;
  // For a resolved (non-pending) line: the split to apply. For a pending line:
  // the SUGGESTED even split (not yet applied — the operator confirms it).
  allocations: XeroLineAllocation[];
  pending: boolean;
  // The rule key — set only for multi-location lines (the case a rule governs).
  signature: string | null;
  // The parsed/confirmed location accounts (for the review UI).
  matchedAccountIds: string[];
};

// Resolve ONE income-eligible line to its allocation. The GROUP anchor (Phase 1c
// — push down to locations by default, nothing silent): when the contact maps to
// a group account —
//   • names ≥2 locations   → suggest an even split across the NAMED set → PENDING
//                            (review) unless a confirmed rule matches;
//   • names exactly 1      → 100% that location, deterministic, no review;
//   • names NO location    → suggest an even split across ALL the group's ACTIVE
//                            operational children → PENDING (review). Replaces the
//                            old silent 100%-to-group fallback. If the group has
//                            NO operational children, fall back to 100%-group
//                            (flagged via kind 'group-fallback') — can't push down.
// A confirmed rule (keyed by the line signature) auto-applies its stored targets
// for BOTH review cases (including a "keep on group" rule); an unruled review
// line is held out of income until confirmed. Non-group contacts keep Phase-1
// behaviour (100% to the contact's account). `getRuleTargets(signature)` returns a
// confirmed rule's target allocations (arbitrary shares) or null.
export function resolveLineAllocation(input: {
  line: { description?: string | null; itemCode?: string | null; repeatingInvoiceId?: string | null };
  contactAccount: AllocationAccount | null;
  descendants: AllocationAccount[];
  fullRoster: AllocationAccount[];
  getRuleTargets: (signature: string) => XeroLineAllocation[] | null;
  bar?: number;
}): LineAllocationResolution {
  const { line, contactAccount, descendants, fullRoster, getRuleTargets } = input;

  if (!contactAccount) {
    return { kind: 'unmapped', allocations: [], pending: false, signature: null, matchedAccountIds: [] };
  }

  // Non-group contact → the contact IS the target (Phase-1 single allocation).
  if (contactAccount.accountType !== 'group') {
    return {
      kind: 'single',
      allocations: [{ accountId: contactAccount.id, shareBps: TOTAL_BPS }],
      pending: false,
      signature: null,
      matchedAccountIds: [contactAccount.id],
    };
  }

  const matched = parseAndMatchLocations(line.description, descendants, fullRoster, input.bar);

  // A single named location resolves deterministically (no split, no rule).
  if (matched.length === 1) {
    return {
      kind: 'single',
      allocations: [{ accountId: matched[0]!.id, shareBps: TOTAL_BPS }],
      pending: false,
      signature: null,
      matchedAccountIds: [matched[0]!.id],
    };
  }

  // Determine the reviewable suggestion set: named-multi, or push-down to the
  // group's operational children when nothing is named.
  let kind: LineAllocationKind;
  let suggestedIds: string[];
  if (matched.length >= 2) {
    kind = 'multi';
    suggestedIds = matched.map((m) => m.id);
  } else {
    const children = operationalActiveDescendants(descendants);
    if (children.length === 0) {
      // Can't push down — the whole line sits on the group account (flagged).
      return {
        kind: 'group-fallback',
        allocations: [{ accountId: contactAccount.id, shareBps: TOTAL_BPS }],
        pending: false,
        signature: null,
        matchedAccountIds: [],
      };
    }
    kind = 'group-all-children';
    suggestedIds = children.map((c) => c.id);
  }

  // Both review cases are governed by a (contact, signature) rule.
  const signature = lineSignature(line);
  const ruleTargets = getRuleTargets(signature);
  if (ruleTargets && ruleTargets.length > 0) {
    return {
      kind,
      allocations: ruleTargets,
      pending: false,
      signature,
      matchedAccountIds: ruleTargets.map((t) => t.accountId),
    };
  }

  // Unruled review line → PENDING. Carry the suggested even split for the review
  // UI, but hold it out of account income until confirmed.
  return {
    kind,
    allocations: buildEvenAllocations(suggestedIds),
    pending: true,
    signature,
    matchedAccountIds: suggestedIds,
  };
}
